import React from "react";
import {gql, useMutation} from "@apollo/client";
import get from "lodash/get";
import classNames from "classnames";
import {string} from "yup";
import {ErrorMessage, Form, FormGroup, SubmitButton, TextInput, Stack} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {useCaptcha} from "@unibuddy/captcha";

import {rootURL} from "shared/Utils/WhereAmI";
import {setMe} from "shared/Auth/actions/authActions";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";
import AuthPanel from "shared/Register/components/AuthPanel/AuthPanel";
import {Body, Container, Header} from "shared/Register/components/VerifyLayout/Layout";
import PageHeader from "shared/Shared/components/PageHeader/PageHeader";

import {PageTitle} from "../../../../components/PageTitle/PageTitle";
import {resendMutation} from "../Verify";
import s from "./VerifyChangeEmail.pcss";
import {SignupImage1} from "../../Register/components/SignupImages";
import {ThemedLink} from "../../../../components/ThemedLink/ThemedLink";
import {IntlNamespaces} from "../../../../translations/types";
import {
    INTL_CANCEL,
    INTL_CHANGE_EMAIL,
    INTL_EMAIL,
    INTL_PAGE_TITLE,
    INTL_UPDATE_EMAIL,
    INTL_UPDATE_EMAIL_HEADER,
} from "../../../../translations/verification";
import {INTL_EMAIL_REQUIRED} from "../../../../translations/validations";

export const updateApplicantMutation = gql`
    mutation UpdateApplicant($email: String!) {
        updateApplicant(email: $email) {
            me {
                anyUser {
                    id
                    email
                }
            }
        }
    }
`;

function VerifyChangeEmail(props) {
    const {reportError} = useErrorReporting();
    const [updateEmail] = useMutation(updateApplicantMutation);
    const [resendEmail] = useMutation(resendMutation);
    const {t} = useTranslation([IntlNamespaces.VERIFICATION, IntlNamespaces.VALIDATIONS]);
    const {getCaptchaToken} = useCaptcha();

    const onSubmit = async ({email}, formActions) => {
        const captureToken = await getCaptchaToken("SendVerificationCode");
        try {
            await updateEmail({
                variables: {
                    email,
                },
            });
            await resendEmail({
                variables: {
                    source: {
                        type: "university",
                        slug: get(props, "universitySlug", ""),
                    },
                    captureToken,
                },
            });
            props.dispatch(
                setMe({
                    ...props.me,
                    anyUser: {
                        ...props.me.anyUser,
                        email,
                    },
                }),
            );
            props.history.replace({
                pathname: `${rootURL(props.location.pathname)}/auth/verify`,
                state: {nextPathname: get(props, "location.state.nextPathname")},
                search: get(props, "location.search"),
            });
        } catch (error) {
            console.log(error);
            formActions.setFieldError("serverError", error.message);
            reportError(error, {tags: {location: "signup"}, level: "fatal"});
        } finally {
            formActions.setSubmitting(false);
        }
    };

    return (
        <Container id="verify-change-email-page">
            <Header>
                <PageTitle
                    title={t(
                        `${IntlNamespaces.VERIFICATION}:${INTL_PAGE_TITLE.key}`,
                        INTL_PAGE_TITLE.defaultValue,
                    )}
                />
            </Header>

            <Body>
                <Stack space="large">
                    <Stack align="center" space="medium">
                        <SignupImage1 />
                        <PageHeader
                            className={s.verifyHeading}
                            title={t(
                                `${IntlNamespaces.VERIFICATION}:${INTL_UPDATE_EMAIL_HEADER.key}`,
                                INTL_UPDATE_EMAIL_HEADER.defaultValue,
                            )}
                        />
                        <p className={classNames(s.textColour, s.subHeadingText)}>
                            {t(
                                `${IntlNamespaces.VERIFICATION}:${INTL_UPDATE_EMAIL.key}`,
                                INTL_UPDATE_EMAIL.defaultValue,
                            )}
                        </p>
                    </Stack>
                    <AuthPanel>
                        <Stack space="large">
                            <Form
                                onSubmit={onSubmit}
                                initialValues={{email: "", serverError: ""}}
                                validationSchema={{
                                    email: string()
                                        .email(
                                            t(
                                                `${IntlNamespaces.VALIDATIONS}:${INTL_EMAIL.key}`,
                                                INTL_EMAIL.defaultValue,
                                            ),
                                        )
                                        .required(
                                            t(
                                                `${IntlNamespaces.VALIDATIONS}:${INTL_EMAIL_REQUIRED.key}`,
                                                INTL_EMAIL_REQUIRED.defaultValue,
                                            ),
                                        ),
                                }}
                            >
                                <Stack space="medium">
                                    <FormGroup
                                        label={t(
                                            `${IntlNamespaces.VERIFICATION}:${INTL_EMAIL.key}`,
                                            INTL_EMAIL.defaultValue,
                                        )}
                                        id="email-input"
                                    >
                                        <TextInput type="email" name="email" />
                                    </FormGroup>
                                    <Stack space="medium" align="center">
                                        <SubmitButton color="primary" id="submit-verify-email">
                                            {t(
                                                `${IntlNamespaces.VERIFICATION}:${INTL_CHANGE_EMAIL.key}`,
                                                INTL_CHANGE_EMAIL.defaultValue,
                                            )}
                                        </SubmitButton>
                                        <ErrorMessage name="serverError" />
                                    </Stack>
                                </Stack>
                            </Form>
                            <p>
                                <ThemedLink
                                    to={{
                                        pathname: `${rootURL(props.location.pathname)}/auth/verify`,
                                        state: {
                                            nextPathname: get(props, "location.state.nextPathname"),
                                        },
                                        search: get(props, "location.search"),
                                    }}
                                >
                                    {t(
                                        `${IntlNamespaces.VERIFICATION}:${INTL_CANCEL.key}`,
                                        INTL_CANCEL.defaultValue,
                                    )}
                                </ThemedLink>
                            </p>
                        </Stack>
                    </AuthPanel>
                </Stack>
            </Body>
        </Container>
    );
}

export default VerifyChangeEmail;
